/**
 * Works out what to hang where.
 *
 * Produces a plan the viewer can execute — which series and image go in which
 * viewport, and how each is transformed — without touching Cornerstone. That
 * separation is what makes hanging testable: the plan is a value, and the only
 * thing the viewer does is carry it out.
 *
 * ## What changes from v1
 *
 * v1 scored every protocol by the fraction of viewports it could fill and hung
 * the highest. This format has no score: groups and categories are matched in
 * array order and the first applicable one wins, modulated by
 * `skipIfOtherGroupHasApplicableHp`. A protocol that fills two of four viewports
 * but sits first now beats one that fills all four and sits second — which is
 * the point, since the author chose that order.
 *
 * ## Tiers
 *
 * Tier 1 (layout, series/instance matching, flips, rotation, scale, alignment,
 * windowing, link-scroll, sync-actions) is planned here. Tier 3 (MPR, quadrant
 * zoom, overview panels) and what remains of Tier 2 are reported in
 * `plan.deferred` so the caller can tell the user what the Advance Viewer would
 * do differently, rather than pretending the hang is complete. Prior *selection*
 * is no longer among them — it happens before a hang, in `priorSelection`.
 */

import { evaluateRuleGroup } from "./ruleEngine";
import { findApplicableProtocol } from "./protocolMatcher";
import { buildInstanceContext, buildSeriesContext, buildStudyContext, unsupportedFields } from "./viewerContext";
import { fieldsUsed } from "./viewerContext";
import { LINK_SCROLL_SERIES, MPR_MIST, SYNC_ACTIONS } from "./protocolEnums";

/** Fields whose value can only come from a loaded image. */
const TAG_FIELD_PREFIX = "instance.";

const needsInstanceTags = (conditions) =>
  [...fieldsUsed(conditions)].some((field) => field.startsWith(TAG_FIELD_PREFIX));

/** The value the Advance Viewer reads as "the middle image of the series". */
const MIDDLE_INSTANCE = "-1";

const isMiddleInstanceRule = (entry) =>
  entry?.field === "instance_number" && String(entry?.value) === MIDDLE_INSTANCE;

/** Walk a condition tree, rewriting leaf rules and leaving the infix separators alone. */
const mapRules = (group, rewrite) =>
  !Array.isArray(group?.rules)
    ? group
    : {
        ...group,
        rules: group.rules.map((entry) => {
          if (typeof entry === "string") return entry;
          return Array.isArray(entry?.rules) ? mapRules(entry, rewrite) : rewrite(entry);
        }),
      };

const hasMiddleInstanceRule = (group) =>
  (group?.rules ?? []).some((entry) => {
    if (typeof entry === "string") return false;
    return Array.isArray(entry?.rules) ? hasMiddleInstanceRule(entry) : isMiddleInstanceRule(entry);
  });

/**
 * `instance_number = -1` means "the middle image", not image number -1: the
 * Advance Viewer rewrites the rule to `floor(instancesCount / 2) + 1` before it
 * evaluates (`changeInstanceNumberIfMiddleInstanceIndicated`). Taken literally
 * the sentinel matches nothing, since positions start at 1.
 *
 * Returns the conditions untouched when the sentinel is absent, which is the
 * usual case — the rewrite only allocates for protocols that ask for it.
 */
function resolveMiddleInstance(conditions, instancesCount) {
  if (!hasMiddleInstanceRule(conditions)) return conditions;
  const middle = Math.max(Math.floor(instancesCount / 2), 0) + 1;
  return mapRules(conditions, (rule) =>
    isMiddleInstanceRule(rule) ? { ...rule, value: String(middle) } : rule
  );
}

/**
 * The viewport properties the basic viewer can carry out, lifted out of the
 * document so the executor does not have to know the format.
 */
function transformsOf(viewport) {
  return {
    rotation: viewport.rotation ?? 0,
    flipHorizontally: Boolean(viewport.flipHorizontally),
    flipVertically: Boolean(viewport.flipVertically),
    scale: viewport.scale ?? "fit_to_screen",
    customScaleValue: viewport.customScaleValue ?? 1,
    alignment: viewport.alignment ?? "center",
    windowWidth: viewport.windowing?.windowWidth ?? null,
    windowCenter: viewport.windowing?.windowCenter ?? null,
  };
}

/**
 * The category's scroll-linking mode, carried through for the executor to set.
 *
 * `NONE` is the unset state a new category starts in, and is reported as null so
 * the viewer keeps whatever mode the radiologist last chose rather than being
 * forced into one by a protocol that never said anything about scrolling.
 */
function linkScrollOf(category) {
  const mode = category.linkScrollSeries;
  return !mode || mode === LINK_SCROLL_SERIES.NONE ? null : mode;
}

/**
 * The category's action-propagation scope, on the same terms as `linkScrollOf`:
 * `NONE` is unset and reported as null, leaving the viewer's own scope alone.
 */
function syncActionsOf(category) {
  const mode = category.syncActions;
  return !mode || mode === SYNC_ACTIONS.NONE ? null : mode;
}

/**
 * Prior settings the category asks for that the basic viewer will not do.
 *
 * Only `openNewComparison` is left: `expandAllPriors` is carried out here (see
 * `plan.expandAllPriors`), and `openAllPriors` / `openMaxPriors` are carried out
 * in the prior-load path, where they belong — they decide which studies get
 * opened, not where a series hangs. See helpers/hangingProtocol/priorSelection.
 */
function deferredPriorsOf(category) {
  return category?.openNewComparison ? ["openNewComparison"] : [];
}

/** What this viewport asks for that the basic viewer will not do. */
function deferredOf(viewport, panel) {
  const reasons = [];
  if (viewport.mprMist && viewport.mprMist !== MPR_MIST.DISABLED) reasons.push(`mprMist=${viewport.mprMist}`);
  if (viewport.mprSeriesInViewer && viewport.mprSeriesInViewer !== "no") reasons.push("mprSeriesInViewer");
  // priorStudy is carried out — see studyIndexFor. otherStudy is not: it addresses
  // a study already open beside this one, and the basic viewer opens one at a time.
  if (viewport.otherStudy ?? panel?.otherStudy) reasons.push("otherStudy");
  if (panel?.isOverview) reasons.push("isOverview");
  if (panel?.quadrantZoomEnabled) reasons.push("quadrantZoom");
  if (panel?.comparisonStudyProtocolEnabled) reasons.push("comparisonStudyProtocol");
  return reasons;
}

/**
 * Which open study a viewport hangs from, as an index into `studies`.
 *
 * The format addresses a prior as `{ conditionIndex, studyIndex }`, both 1-based:
 * *which* of the category's `priorStudyConditions` entries, then *which* of the
 * studies that satisfied it. Neither is an index into the patient's studies
 * directly, which is why `byCondition` has to come from the same selection pass
 * that filtered them — see helpers/hangingProtocol/priorSelection.
 *
 * A viewport's own address beats the panel's, being the more specific of the two.
 *
 * @returns the index, 0 for the current study, or null when the address cannot
 *          be resolved — no such condition, or fewer priors matched it than the
 *          protocol expected.
 */
export function studyIndexFor(viewport, panel, byCondition) {
  const address = viewport?.priorStudy ?? panel?.priorStudy ?? null;
  if (!address) return 0;

  const matches = byCondition?.get(address.conditionIndex ?? 1) ?? [];
  return matches[(address.studyIndex ?? 1) - 1] ?? null;
}

/**
 * Find the first instance of `series` satisfying `instanceConditions`.
 *
 * Returns 0 when the slot is empty, so an unconstrained viewport shows the first
 * image. That is deliberately NOT what MedDream does: its `getValidInstance`
 * returns false for an empty `rules` array, so the Advance Viewer matches no
 * instance and hangs nothing — see `createFirstInstanceConditions` in hpDocument.
 *
 * We keep the forgiving reading here on purpose. Diverging costs one blank cell
 * in the Advance Viewer; converging would blank the same cell in BOTH viewers and
 * make an already-published protocol look newly broken. The shape is headed off
 * where it is created instead — new viewports carry `instance_number = 1`, and
 * `isDeadViewport` flags the ones that predate that default.
 */
async function findInstanceIndex(series, viewport, seriesContext, resolveInstanceTags, address, options) {
  const instances = series.instances ?? [];
  const conditions = resolveMiddleInstance(
    viewport.instanceConditions,
    series.instancesCount ?? instances.length
  );
  const operandCount = conditions?.rules?.length ?? 0;
  if (operandCount === 0) return 0;

  const wantsTags = needsInstanceTags(conditions);

  for (let index = 0; index < instances.length; index += 1) {
    const tags =
      wantsTags && resolveInstanceTags
        ? await resolveInstanceTags(address.studyIndex, address.seriesIndex, index)
        : null;
    const context = buildInstanceContext(instances[index], {
      seriesContext,
      tags,
      matchedIndex: index + 1,
    });
    if (evaluateRuleGroup(conditions, context, options)) return index;
  }
  return -1;
}

/**
 * Plan a hang.
 *
 * @param document  a hangingProtocolsV2 document
 * @param study     normalised study — see viewerContext. The protocol is matched
 *                  against this one; it is what the radiologist opened.
 * @param studies   every open study, index-aligned to the viewer's priorStudyData
 *                  (0 is the current study, 1..n its priors). Defaults to
 *                  `[study]`, which is a viewer with no priors open.
 * @param byCondition  Map(conditionIndex -> [index into `studies`]) from the
 *                  prior selection pass, needed to resolve `viewport.priorStudy`
 * @param session   { openStudiesCount, patientStudiesCount, device }
 * @param resolveInstanceTags  async (studyIndex, seriesIndex, instanceIndex) =>
 *        tags, called only when an instance condition references a tag field
 * @returns a plan, or null when no protocol applies
 */
/**
 * When two viewports carry conditions that both accept the same series, each
 * independently takes its first match — so the series can appear in both.
 *
 * This is a product decision, and it keeps the v1 engine's behaviour, which
 * computed every viewport's match independently. A viewport's conditions are a
 * predicate over series, not a claim on one: if an author wants distinct series
 * per viewport they say so, with `series_number` or `matched_series_number`.
 *
 * Set false to make viewports consume series in turn instead.
 */
export const DEFAULT_REUSE_SERIES = true;

export async function planHang(
  document,
  {
    study,
    studies = null,
    byCondition = null,
    session = {},
    resolveInstanceTags = null,
    options = {},
    reuseSeries = DEFAULT_REUSE_SERIES,
  } = {}
) {
  const studyContext = buildStudyContext(study, session);

  const match = findApplicableProtocol(document, studyContext, {
    device: session.device ?? null,
    ...options,
  });
  if (!match) return null;

  const { group, category } = match;
  const panels = category.panelsLayout?.panels ?? [];

  // The basic viewer has one flat grid, so a multi-panel protocol is flattened:
  // the first panel's grid is used and the rest reported as deferred.
  const panel = panels[0] ?? null;
  const layout = panel?.layout ?? { rows: 1, columns: 1, viewports: [] };
  const viewports = layout.viewports ?? [];

  const openStudies = studies?.length ? studies : [study];
  /**
   * Study-scope fields describe the study the series belongs to, so a viewport
   * hanging a prior matches against that prior's context, not the open study's.
   * Built lazily — most protocols never address a prior at all.
   */
  const studyContexts = new Map([[0, studyContext]]);
  const contextFor = (studyIndex) => {
    if (!studyContexts.has(studyIndex)) {
      studyContexts.set(studyIndex, buildStudyContext(openStudies[studyIndex], session));
    }
    return studyContexts.get(studyIndex);
  };

  const assignments = [];
  const deferred = new Set();
  /**
   * "studyIndex:seriesIndex" -> the 1-based order in which it was first matched,
   * which is what `matched_series_number` means. Keyed rather than counted so a
   * series keeps its number when several viewports show it.
   */
  const matchedNumbers = new Map();

  if (panels.length > 1) deferred.add("multiplePanels");
  unsupportedFields(category).forEach((field) => deferred.add(`field:${field}`));
  deferredPriorsOf(category).forEach((reason) => deferred.add(reason));

  for (let viewportIndex = 0; viewportIndex < viewports.length; viewportIndex += 1) {
    const viewport = viewports[viewportIndex];
    deferredOf(viewport, panel).forEach((reason) => deferred.add(reason));

    const studyIndex = studyIndexFor(viewport, panel, byCondition);
    // The protocol asked for a prior that is not open — fewer matched its
    // condition than it expected, or selection never ran. Leaving the viewport
    // empty is right: filling it from the current study would silently show the
    // radiologist today's images where they asked for a comparison.
    if (studyIndex === null || !openStudies[studyIndex]) {
      if (viewport.priorStudy ?? panel?.priorStudy) deferred.add("priorStudyUnavailable");
      continue;
    }

    const series = openStudies[studyIndex]?.series ?? [];
    const context = contextFor(studyIndex);
    let assigned = null;

    for (let seriesIndex = 0; seriesIndex < series.length; seriesIndex += 1) {
      // Presentation states, key objects and SRs are not hangable, and the
      // Advance Viewer does not offer them as series either. Left in, a viewport
      // with no series condition ("any series") would take whichever of them
      // sorts first — which is exactly where they sort.
      if (series[seriesIndex]?.isImage === false) continue;

      const key = `${studyIndex}:${seriesIndex}`;
      if (!reuseSeries && matchedNumbers.has(key)) continue;

      const seriesContext = buildSeriesContext(series[seriesIndex], {
        studyContext: context,
        // Its existing number if already matched, otherwise the one it would take.
        matchedIndex: matchedNumbers.get(key) ?? matchedNumbers.size + 1,
      });

      if (!evaluateRuleGroup(viewport.seriesConditions, seriesContext, options)) continue;

      const instanceIndex = await findInstanceIndex(
        series[seriesIndex],
        viewport,
        seriesContext,
        resolveInstanceTags,
        { studyIndex, seriesIndex },
        options
      );
      if (instanceIndex === -1) continue;

      assigned = { seriesIndex, instanceIndex };
      if (!matchedNumbers.has(key)) matchedNumbers.set(key, matchedNumbers.size + 1);
      break;
    }

    if (assigned) {
      assignments.push({
        viewportIndex,
        row: viewport.row,
        column: viewport.column,
        // Which open study the series came from — 0 is the study being reported on.
        studyIndex,
        seriesIndex: assigned.seriesIndex,
        instanceIndex: assigned.instanceIndex,
        transforms: transformsOf(viewport),
      });
    }
  }

  return {
    group,
    category,
    groupId: group.id,
    categoryId: category.id,
    name: category.name,
    fallback: match.fallback,
    layout: { rows: layout.rows ?? 1, columns: layout.columns ?? 1 },
    // OFF / MANUAL / AUTO / POSITION, or null when the category says nothing.
    linkScrollSeries: linkScrollOf(category),
    // OFF / MANUAL / SERIES / LINKED, or null when the category says nothing.
    syncActions: syncActionsOf(category),
    // Whether each prior study's thumbnail list starts expanded rather than
    // collapsed to its first series.
    expandAllPriors: Boolean(category.expandAllPriors),
    assignments,
    // A protocol can legitimately fill only some viewports; the caller decides
    // whether a partial hang is worth applying.
    matchedViewports: assignments.length,
    totalViewports: viewports.length,
    deferred: [...deferred],
  };
}

/**
 * Plans for every applicable protocol, in precedence order — what a "choose a
 * hanging protocol" menu needs. Each is planned in full so the menu can show
 * how much of each layout would actually fill.
 */
export async function planAllApplicable(document, context) {
  const { listApplicableProtocols } = await import("./protocolMatcher");
  const studyContext = buildStudyContext(context.study, context.session ?? {});
  const applicable = listApplicableProtocols(document, studyContext, {
    device: context.session?.device ?? null,
    ...(context.options ?? {}),
  });

  const plans = [];
  for (const entry of applicable) {
    // Re-plan against a single-category document so ordering cannot pick another.
    const scoped = { ...document, hp: [{ ...entry.group, categories: [entry.category] }], hpDrag: [], hpComparison: [] };
    const plan = await planHang(scoped, context);
    if (plan) plans.push(plan);
  }
  return plans;
}
